import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/contextAuth.js";
import {
  getConfigAssets,
  getConfigScanProgress,
} from "../../../api/get/gcp/get-config.js";
import { scanConfig } from "../../../api/post/post-gcp.js";

const useGcpConfig = () => {
  const { user, userId } = useAuth();
  const [index, setIndex] = useState(0);
  const [projectId, setProjectId] = useState("");
  const [serviceAccountKey, setServiceAccountKey] = useState("");
  const [serviceAccountFileName, setServiceAccountFileName] = useState("");

  const [configLoading, setConfigLoading] = useState(false);
  const [configScanLoading, setConfigScanLoading] = useState(false);
  const [configScanProgress, setConfigScanProgress] = useState(null);
  const [configView, setConfigView] = useState(false);

  const [configAssets, setConfigAssets] = useState([]);
  const [cGcpConfigAll, setCgcpConfigAll] = useState(null);
  const [saveScore, setSaveScore] = useState(0);
  const [error, setError] = useState(null);

  const fetchConfigAssets = useCallback(async (requestedUserId) => {
    try {
      const assets = await getConfigAssets(requestedUserId);
      setConfigAssets(assets || []);
      setCgcpConfigAll(assets?.[0]?.misconfig || null);
      setSaveScore(assets?.[0]?.ss ?? 0);
      return assets;
    } catch (fetchError) {
      console.error("Error loading GCP config assets:", fetchError);
      setConfigAssets([]);
      setCgcpConfigAll(null);
      setSaveScore(0);
      throw fetchError;
    }
  }, []);

  useEffect(() => {
    if (!user || !userId) return;
    (async () => {
      try {
        setConfigLoading(true);
        await fetchConfigAssets(userId);
      } catch (fetchError) {
        console.error("Error in GCP initData:", fetchError);
      } finally {
        setConfigLoading(false);
      }
    })();
  }, [user, userId, fetchConfigAssets]);

  const initData = async () => {
    if (!user || !userId) return;
    return fetchConfigAssets(userId);
  };

  const refreshLatestAssessment = async (expectedScanId) => {
    let latestAssets = [];
    for (let attempt = 0; attempt < 5; attempt += 1) {
      latestAssets = await fetchConfigAssets(userId);
      if (!expectedScanId || latestAssets?.[0]?.gcp_uuid === expectedScanId) {
        return latestAssets;
      }
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    throw new Error(
      "The GCP assessment completed, but its newest result is not available yet"
    );
  };

  const handleServiceAccountFile = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setError(null);
    try {
      if (file.size > 100 * 1024) {
        throw new Error("The GCP service account key file is too large");
      }

      const content = await file.text();
      const parsed = JSON.parse(content);
      if (
        parsed?.type !== "service_account" ||
        !parsed?.client_email ||
        !parsed?.private_key ||
        !parsed?.project_id
      ) {
        throw new Error("Select a valid GCP service account JSON key file");
      }

      setServiceAccountKey(content);
      setServiceAccountFileName(file.name);
      // The key's project_id is the safest default and replaces stale values
      // left behind by an earlier assessment or ScoutSuite report name.
      setProjectId(parsed.project_id);
    } catch (fileError) {
      setServiceAccountKey("");
      setServiceAccountFileName("");
      setError({
        message: fileError.message || "Unable to read the service account key",
        card: false,
      });
    } finally {
      if (event?.target) event.target.value = "";
    }
  };

  const handleConfigScan = async (event) => {
    if (event) event.preventDefault();
    setConfigScanLoading(true);
    setError(null);
    setConfigScanProgress({
      status: "running",
      stage: "starting",
      progress: 1,
      message: "Starting GCP assessment",
    });

    const progressTimer = window.setInterval(async () => {
      try {
        const progress = await getConfigScanProgress(userId);
        if (progress) setConfigScanProgress(progress);
      } catch (progressError) {
        console.warn("Unable to refresh GCP scan progress:", progressError);
      }
    }, 1000);

    try {
      if (!userId) {
        throw new Error("User ID does not exist. Please sign in again.");
      }
      if (!projectId || !serviceAccountKey) {
        throw new Error(
          "Provide a GCP project ID and service account JSON key file"
        );
      }
      if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(projectId.trim())) {
        throw new Error(
          "Enter a valid GCP project ID (for example, my-project-123), not a project name, number, or ScoutSuite report ID"
        );
      }

      const result = await scanConfig(userId, {
        projectId,
        serviceAccountKey,
      });
      const finalProgress = await getConfigScanProgress(userId);
      if (finalProgress) setConfigScanProgress(finalProgress);
      await refreshLatestAssessment(result?.data?.scanId);
      setConfigView(true);
    } catch (scanError) {
      setConfigView(false);
      setError({
        message:
          scanError.message ||
          "GCP assessment failed. Check the project and service account.",
        card: scanError.card || false,
        code: scanError.code || "UNKNOWN_ERROR",
        setupUrl: scanError.setupUrl || "",
      });
    } finally {
      window.clearInterval(progressTimer);
      setConfigScanLoading(false);
      setServiceAccountKey("");
      setServiceAccountFileName("");
    }
  };

  const resetState = () => {
    setIndex(0);
    setProjectId("");
    setServiceAccountKey("");
    setServiceAccountFileName("");
    setConfigLoading(false);
    setConfigScanLoading(false);
    setConfigScanProgress(null);
    setConfigView(false);
    setConfigAssets([]);
    setCgcpConfigAll(null);
    setSaveScore(0);
    setError(null);
  };

  return {
    index,
    setIndex,
    projectId,
    setProjectId,
    serviceAccountKey,
    setServiceAccountKey,
    serviceAccountFileName,
    setServiceAccountFileName,
    configLoading,
    setConfigLoading,
    configScanLoading,
    setConfigScanLoading,
    configScanProgress,
    setConfigScanProgress,
    configView,
    setConfigView,
    configAssets,
    setConfigAssets,
    cGcpConfigAll,
    setCgcpConfigAll,
    saveScore,
    setSaveScore,
    error,
    setError,
    handleConfigScan,
    handleServiceAccountFile,
    fetchConfigAssets,
    initData,
    resetState,
  };
};

export default useGcpConfig;
